"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ADDRESS_NORMALIZATION_SCHEMA_VERSION = void 0;
exports.addressNormalizationVersions = addressNormalizationVersions;
exports.normalizeAddressInput = normalizeAddressInput;
exports.buildDeterministicAddressCandidate = buildDeterministicAddressCandidate;
exports.validateAddressCandidateFaithfulness = validateAddressCandidateFaithfulness;
exports.currentAddressNormalization = currentAddressNormalization;
exports.reviewedAddressQuery = reviewedAddressQuery;
exports.addressReviewDocumentPath = addressReviewDocumentPath;
exports.ADDRESS_NORMALIZATION_SCHEMA_VERSION = 1;
// Keep this intentionally small and reviewed. Adding an alias is a data-quality
// decision because it permits a model to rewrite that city without guessing.
const REVIEWED_CITIES = [
    { canonical: 'תל אביב-יפו', aliases: ['תל אביב-יפו', 'תל אביב יפו', 'תל אביב'] },
    { canonical: 'ירושלים', aliases: ['ירושלים', 'jerusalem'] },
    { canonical: 'חיפה', aliases: ['חיפה', 'haifa'] },
    { canonical: 'רמת גן', aliases: ['רמת גן', 'רמת-גן'] },
    { canonical: 'פתח תקווה', aliases: ['פתח תקווה', 'פתח-תקווה'] },
    { canonical: 'ראשון לציון', aliases: ['ראשון לציון', 'ראשון-לציון'] },
    { canonical: 'בני ברק', aliases: ['בני ברק', 'בני-ברק'] },
    { canonical: 'אשדוד', aliases: ['אשדוד'] },
    { canonical: 'נתניה', aliases: ['נתניה'] },
    { canonical: 'באר שבע', aliases: ['באר שבע', 'באר-שבע'] },
    { canonical: 'הרצליה', aliases: ['הרצליה'] },
    { canonical: 'רעננה', aliases: ['רעננה'] },
    { canonical: 'כפר סבא', aliases: ['כפר סבא', 'כפר-סבא'] },
    { canonical: 'רחובות', aliases: ['רחובות'] },
];
const CITY_ALIAS_VERSION = '2026-08-05-v1';
const NORMALIZER_VERSION = '2026-08-05-v1';
function addressNormalizationVersions() {
    return {
        normalizerVersion: NORMALIZER_VERSION,
        aliasVersion: CITY_ALIAS_VERSION,
    };
}
function comparable(value) {
    return value
        .normalize('NFKC')
        .toLocaleLowerCase('he-IL')
        .replace(/[־–—-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
function display(value) {
    return value
        .normalize('NFKC')
        .replace(/[־–—]/g, '-')
        .replace(/\s+/g, ' ')
        .trim();
}
function containsWholeComponent(value, component) {
    let start = value.indexOf(component);
    while (start >= 0) {
        const before = value[start - 1];
        const after = value[start + component.length];
        const isWord = (character) => Boolean(character && /[0-9A-Za-z\u0590-\u05ff]/u.test(character));
        if (!isWord(before) && !isWord(after))
            return true;
        start = value.indexOf(component, start + 1);
    }
    return false;
}
function normalizeAddressInput(value) {
    return display(value).replace(/\s*,\s*/g, ', ');
}
function cityFor(value) {
    const normalized = comparable(value);
    return REVIEWED_CITIES
        .map((city) => ({
        city,
        aliases: city.aliases.map(comparable).sort((a, b) => b.length - a.length),
    }))
        .sort((a, b) => b.aliases[0].length - a.aliases[0].length)
        .find(({ aliases }) => aliases.some((alias) => containsWholeComponent(normalized, alias)))?.city ?? null;
}
function cityFromText(value) {
    const reviewed = cityFor(value);
    if (!reviewed)
        return null;
    return reviewed.canonical;
}
function exactReviewedCity(value) {
    const reviewed = cityFor(value);
    if (!reviewed)
        return null;
    return reviewed.aliases.some((alias) => comparable(alias) === comparable(value))
        ? reviewed.canonical
        : null;
}
function cityFromAddress(value) {
    const parts = value
        .split(/[,;|]/u)
        .map(display)
        .filter(Boolean);
    const numberedIndex = parts.findIndex((part) => numberFromText(part) !== null);
    if (numberedIndex >= 0) {
        const segmentedCity = parts
            .filter((_, index) => index !== numberedIndex)
            .map(cityFromText)
            .find((city) => Boolean(city));
        if (segmentedCity)
            return segmentedCity;
        // Some legacy records omit separators and append the city after the
        // house number. Only inspect that suffix; a city name in the street
        // prefix (for example, "רחוב תל אביב 12") is not enough evidence.
        const numberedPart = parts[numberedIndex];
        const numberMatch = /(?:^|\s)(\d+[א-תA-Za-z]?)(?=\s|$|[,;|/])/u.exec(numberedPart);
        if (numberMatch) {
            return cityFromText(numberedPart.slice(numberMatch.index + numberMatch[0].length));
        }
    }
    return null;
}
function stripStreetPrefix(value) {
    return value
        .replace(/^\s*(?:רחוב|רח['׳]?|street)\s+/i, '')
        .replace(/\s+(?:דירה|דירת|קומה|apt|apartment|floor)\b.*$/i, '')
        .replace(/^[,;|\s]+|[,;|\s]+$/g, '')
        .trim();
}
function numberFromText(value) {
    return value.match(/(?:^|\s)(\d+[א-תA-Za-z]?)(?=\s|$|[,;|/])/u)?.[1] ?? null;
}
function queryFor(candidate) {
    return `${candidate.street} ${candidate.houseNumber}, ${candidate.city}`;
}
function buildDeterministicAddressCandidate(rawAddress) {
    if (typeof rawAddress !== 'string' || !rawAddress.trim())
        return null;
    const parts = rawAddress
        .split(/[,;|]/u)
        .map(display)
        .filter(Boolean);
    if (parts.length < 2)
        return null;
    const numberedIndex = parts.findIndex((part) => numberFromText(part) !== null);
    if (numberedIndex < 0)
        return null;
    const streetPart = parts[numberedIndex];
    const houseNumber = numberFromText(streetPart);
    if (!houseNumber)
        return null;
    const street = stripStreetPrefix(streetPart.slice(0, streetPart.indexOf(houseNumber)));
    if (!street)
        return null;
    const city = parts
        .filter((_, index) => index !== numberedIndex)
        .map(cityFromText)
        .find((value) => Boolean(value));
    if (!city)
        return null;
    const candidate = { street, houseNumber, city, countryCode: 'IL' };
    return { ...candidate, geocodeQuery: queryFor(candidate) };
}
function rawHouseNumbers(rawAddress) {
    // Floor and apartment numbers are not evidence for a building number.
    const buildingText = rawAddress.replace(/(?:^|[\s,;]+)(?:קומה|דירה|floor|apt\.?|apartment)\s*:?\s*-?\d+[א-תA-Za-z]?(?=$|[\s,;])/giu, '');
    return [...buildingText.matchAll(/(?:^|\s)(\d+[א-תA-Za-z]?)(?=\s|$|[,;|/])/gu)]
        .map((match) => match[1])
        .filter((value) => Boolean(value));
}
function validateAddressCandidateFaithfulness(rawAddress, candidate) {
    if (candidate.countryCode !== 'IL') {
        return { ok: false, reasonCode: 'candidate_country_not_supported' };
    }
    if (candidate.street.trim().length < 2) {
        return { ok: false, reasonCode: 'candidate_street_missing' };
    }
    if (/\d/u.test(candidate.street)) {
        return { ok: false, reasonCode: 'candidate_street_contains_house_number' };
    }
    if (!candidate.city.trim()) {
        return { ok: false, reasonCode: 'candidate_city_missing' };
    }
    if (!rawHouseNumbers(rawAddress).some((value) => comparable(value) === comparable(candidate.houseNumber))) {
        return { ok: false, reasonCode: 'candidate_house_number_not_supported' };
    }
    const rawComparable = comparable(rawAddress);
    if (!containsWholeComponent(rawComparable, comparable(candidate.street))) {
        return { ok: false, reasonCode: 'candidate_street_not_supported' };
    }
    const canonicalRawCity = cityFromAddress(rawAddress);
    const canonicalCandidateCity = exactReviewedCity(candidate.city);
    if (!canonicalRawCity || canonicalRawCity !== canonicalCandidateCity) {
        return { ok: false, reasonCode: 'candidate_city_not_supported' };
    }
    if (comparable(candidate.geocodeQuery) !== comparable(queryFor(candidate))) {
        return { ok: false, reasonCode: 'candidate_query_mismatch' };
    }
    return { ok: true };
}
/** Only server-owned results for the current source revision can guide navigation.
 * Older results stay visible as stale data but cannot survive an address edit. */
function currentAddressNormalization(rawAddress, result) {
    return result?.sourceAddress === normalizeAddressInput(rawAddress) ? result : undefined;
}
function reviewedAddressQuery(rawAddress, result) {
    const current = currentAddressNormalization(rawAddress, result);
    if (current?.state !== 'candidate' || !current.candidate)
        return null;
    return current.review || current.validation?.status === 'matched' ? current.candidate.geocodeQuery : null;
}
function addressReviewDocumentPath(source) {
    const collection = source.sourceType === 'deal' ? 'deals'
        : source.sourceType === 'visit' ? 'scheduleVisits' : `${source.sourceType.replace('_report', '')}_reports`;
    return source.legacy ? `${collection}/${source.sourceId}` : `companies/${source.companyId}/${collection}/${source.sourceId}`;
}
